import { ReactQueryKeys } from '@/utils/react-query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAgents,
  getAgent,
  createAgent,
  updateAgent,
  deleteAgent,
  publishAgent,
  syncAgent,
  unpublishAgent,
  getAgentUsage,
  testCall,
  addKbText,
  addKbUrl,
  addKbFile,
  removeKbDoc,
  checkKbStatus,
  getPrefillInstructions,
  getTransferableDocuments,
  transferKb,
  updateCallHandler,
  updateCommandCenterHandoff,
  getLanguageVoices,
  updateLanguageVoices,
  getTransferAnnouncements,
  generateTransferAnnouncements,
  getFillerClips,
  getAudioClipTexts,
  getVoiceHealth,
  updateAudioClipText,
  destroyAudioClipText,
} from '@/services/agents-service/agents.service';
import {
  CreateAgentPayload,
  UpdateAgentPayload,
  AddKbTextPayload,
  AddKbUrlPayload,
  AddKbFilePayload,
  TransferKbPayload,
  UpdateCallHandlerPayload,
  UpdateCommandCenterHandoffPayload,
  UpdateLanguageVoicesPayload,
} from '@/services/agents-service/agents.service.types';

export const useAgents = (companyId: number | undefined) => {
  const { data, isPending } = useQuery({
    queryKey: ReactQueryKeys.AGENTS(companyId),
    queryFn: () => getAgents({ company_id: companyId! }),
    enabled: !!companyId,
  });

  return { agents: data?.conversational_agents, isPending };
};

export const useAgent = (id: string, companyId: number | undefined) => {
  const { data, isPending } = useQuery({
    queryKey: ReactQueryKeys.AGENT(id),
    queryFn: () => getAgent(id, { company_id: companyId! }),
    enabled: !!companyId && !!id,
  });

  return { agent: data?.conversational_agent, isPending };
};

export const useCreateAgent = (companyId: number | undefined) => {
  const queryClient = useQueryClient();

  const { mutate: create, isPending } = useMutation({
    mutationFn: (data: Omit<CreateAgentPayload, 'company_id'>) =>
      createAgent({ ...data, company_id: companyId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENTS(companyId) });
    },
  });

  return { create, isPending };
};

export const useUpdateAgent = (id: string, companyId: number | undefined) => {
  const queryClient = useQueryClient();

  const { mutate: update, isPending } = useMutation({
    mutationFn: (data: UpdateAgentPayload) =>
      updateAgent(id, { company_id: companyId! }, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENT(id) });
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENTS(companyId) });
    },
  });

  return { update, isPending };
};

export const useDeleteAgent = (companyId: number | undefined) => {
  const queryClient = useQueryClient();

  const { mutate: remove, isPending } = useMutation({
    mutationFn: (id: number) => deleteAgent(id, { company_id: companyId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENTS(companyId) });
    },
  });

  return { remove, isPending };
};

export const usePublishAgent = (id: string, companyId: number | undefined) => {
  const queryClient = useQueryClient();

  const { mutate: publish, isPending } = useMutation({
    mutationFn: () => publishAgent(id, { company_id: companyId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENT(id) });
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENTS(companyId) });
    },
  });

  return { publish, isPending };
};

export const useSyncAgent = (id: string, companyId: number | undefined) => {
  const queryClient = useQueryClient();

  const { mutate: sync, isPending } = useMutation({
    mutationFn: () => syncAgent(id, { company_id: companyId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENT(id) });
    },
  });

  return { sync, isPending };
};

export const useUnpublishAgent = (id: string, companyId: number | undefined) => {
  const queryClient = useQueryClient();

  const { mutate: unpublish, isPending } = useMutation({
    mutationFn: () => unpublishAgent(id, { company_id: companyId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENT(id) });
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENTS(companyId) });
    },
  });

  return { unpublish, isPending };
};

export const useAgentUsage = (id: string, companyId: number | undefined) => {
  const { data, isPending } = useQuery({
    queryKey: ReactQueryKeys.AGENT_USAGE(id),
    queryFn: () => getAgentUsage(id, { company_id: companyId! }),
    enabled: !!companyId && !!id,
  });

  return { usage: data, isPending };
};

export const useTestCall = (id: string, companyId: number | undefined) => {
  const { mutate: makeTestCall, isPending } = useMutation({
    mutationFn: (phoneNumber: string) =>
      testCall(id, { company_id: companyId!, phone_number: phoneNumber }),
  });

  return { makeTestCall, isPending };
};

export const useAddKbDocument = (id: string, companyId: number | undefined) => {
  const queryClient = useQueryClient();

  const addText = useMutation({
    mutationFn: (data: Omit<AddKbTextPayload, 'company_id'>) =>
      addKbText(id, { ...data, company_id: companyId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENT(id) });
    },
  });

  const addUrl = useMutation({
    mutationFn: (data: Omit<AddKbUrlPayload, 'company_id'>) =>
      addKbUrl(id, { ...data, company_id: companyId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENT(id) });
    },
  });

  const addFile = useMutation({
    mutationFn: (data: Omit<AddKbFilePayload, 'company_id'>) =>
      addKbFile(id, { ...data, company_id: companyId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENT(id) });
    },
  });

  return {
    addText: addText.mutate,
    addUrl: addUrl.mutate,
    addFile: addFile.mutate,
    isPending: addText.isPending || addUrl.isPending || addFile.isPending,
  };
};

export const useRemoveKbDocument = (id: string, companyId: number | undefined) => {
  const queryClient = useQueryClient();

  const { mutate: remove, isPending } = useMutation({
    mutationFn: (docId: number) => removeKbDoc(id, docId, { company_id: companyId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENT(id) });
    },
  });

  return { remove, isPending };
};

export const useCheckKbStatus = (id: string, companyId: number | undefined) => {
  const queryClient = useQueryClient();

  const { mutate: checkStatus, isPending } = useMutation({
    mutationFn: (docId: number) => checkKbStatus(id, docId, { company_id: companyId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENT(id) });
    },
  });

  return { checkStatus, isPending };
};

export const usePrefillInstructions = (companyId: number | undefined) => {
  const { data, isPending } = useQuery({
    queryKey: ReactQueryKeys.AGENT_PREFILL(companyId),
    queryFn: () => getPrefillInstructions({ company_id: companyId! }),
    enabled: !!companyId,
  });

  return { prefill: data, isPending };
};

export const useTransferableDocuments = (id: string, companyId: number | undefined) => {
  const { data, isPending } = useQuery({
    queryKey: ReactQueryKeys.AGENT_TRANSFERABLE_DOCS(id, companyId),
    queryFn: () => getTransferableDocuments(id, { company_id: companyId! }),
    enabled: !!companyId && !!id,
  });

  return { documents: data?.documents, isPending };
};

export const useTransferKb = (id: string, companyId: number | undefined) => {
  const queryClient = useQueryClient();

  const { mutate: transfer, isPending } = useMutation({
    mutationFn: (data: Omit<TransferKbPayload, 'company_id'>) =>
      transferKb(id, { ...data, company_id: companyId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENT(id) });
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENT_TRANSFERABLE_DOCS(id, companyId) });
    },
  });

  return { transfer, isPending };
};

export const useUpdateCallHandler = (companyId: number | undefined) => {
  const queryClient = useQueryClient();

  const { mutate: switchHandler, isPending } = useMutation({
    mutationFn: (data: Omit<UpdateCallHandlerPayload, 'company_id'>) =>
      updateCallHandler({ ...data, company_id: companyId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENTS(companyId) });
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.COMPANIES() });
    },
  });

  return { switchHandler, isPending };
};

// Toggle for `Company.command_center_handoff_enabled` — the migration
// flag that routes generic call-center rai handoffs into the Command
// Center queue instead of PSTN dial. Invalidates COMPANIES so the
// settings page reflects the new value immediately.
export const useUpdateCommandCenterHandoff = (companyId: number | undefined) => {
  const queryClient = useQueryClient();

  const { mutate: switchHandoff, isPending } = useMutation({
    mutationFn: (data: Omit<UpdateCommandCenterHandoffPayload, 'company_id'>) =>
      updateCommandCenterHandoff({ ...data, company_id: companyId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.COMPANIES() });
    },
  });

  return { switchHandoff, isPending };
};

export const useLanguageVoices = (companyId: number | undefined) => {
  const { data, isPending } = useQuery({
    queryKey: ReactQueryKeys.LANGUAGE_VOICES(companyId),
    queryFn: () => getLanguageVoices({ company_id: companyId! }),
    enabled: !!companyId,
  });

  return { languageVoices: data?.language_voices, isPending };
};

export const useUpdateLanguageVoices = (companyId: number | undefined) => {
  const queryClient = useQueryClient();

  const { mutate: updateLanguages, isPending } = useMutation({
    mutationFn: (data: Omit<UpdateLanguageVoicesPayload, 'company_id'>) =>
      updateLanguageVoices({ ...data, company_id: companyId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.LANGUAGE_VOICES(companyId) });
    },
  });

  return { updateLanguages, isPending };
};

export const useTransferAnnouncements = (id: string, companyId: number | undefined) => {
  const { data, isPending, refetch } = useQuery({
    queryKey: ReactQueryKeys.AGENT_TRANSFER_ANNOUNCEMENTS(id, companyId),
    queryFn: () => getTransferAnnouncements(id, { company_id: companyId! }),
    enabled: !!companyId && !!id,
  });

  return { announcements: data?.announcements, isPending, refetch };
};

export const useGenerateTransferAnnouncements = (id: string, companyId: number | undefined) => {
  const queryClient = useQueryClient();

  const { mutate: generate, isPending } = useMutation({
    mutationFn: (force?: boolean) =>
      generateTransferAnnouncements(id, { company_id: companyId!, force }),
    onSuccess: () => {
      // Delay refetch slightly so the job has time to start uploading.
      // Generate also chains GenerateFillerClipsJob server-side, so refresh
      // both panels.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENT_TRANSFER_ANNOUNCEMENTS(id, companyId) });
        queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENT_FILLER_CLIPS(id, companyId) });
      }, 3000);
    },
  });

  return { generate, isPending };
};

export const useFillerClips = (id: string, companyId: number | undefined) => {
  const { data, isPending, refetch } = useQuery({
    queryKey: ReactQueryKeys.AGENT_FILLER_CLIPS(id, companyId),
    queryFn: () => getFillerClips(id, { company_id: companyId! }),
    enabled: !!companyId && !!id,
  });

  return { clips: data?.clips, isPending, refetch };
};

export const useVoiceHealth = (id: string, companyId: number | undefined) => {
  const { data, isPending, refetch } = useQuery({
    queryKey: ReactQueryKeys.AGENT_VOICE_HEALTH(id, companyId),
    queryFn: () => getVoiceHealth(id, { company_id: companyId! }),
    enabled: !!companyId && !!id,
    // Workspace state can change outside the dashboard (admin deletes a voice
    // on ElevenLabs directly). 60s staleness keeps the lookup feeling live
    // without hammering the upstream API every time the tab repaints.
    staleTime: 60_000,
  });

  return { health: data?.health, workspaceTotal: data?.workspace_total, error: data?.error, isPending, refetch };
};

export const useAudioClipTexts = (id: string, companyId: number | undefined) => {
  const { data, isPending, refetch } = useQuery({
    queryKey: ReactQueryKeys.AGENT_AUDIO_CLIP_TEXTS(id, companyId),
    queryFn: () => getAudioClipTexts(id, { company_id: companyId! }),
    enabled: !!companyId && !!id,
  });

  return { texts: data?.texts, isPending, refetch };
};

export const useUpdateAudioClipText = (id: string, companyId: number | undefined) => {
  const queryClient = useQueryClient();
  const { mutateAsync: updateText, isPending } = useMutation({
    mutationFn: (input: { clip_type: 'disclaimer' | 'transferring' | 'filler'; text: string }) =>
      updateAudioClipText(id, { company_id: companyId!, ...input }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENT_AUDIO_CLIP_TEXTS(id, companyId) });
    },
  });

  return { updateText, isPending };
};

export const useResetAudioClipText = (id: string, companyId: number | undefined) => {
  const queryClient = useQueryClient();
  const { mutateAsync: resetText, isPending } = useMutation({
    mutationFn: (clip_type: 'disclaimer' | 'transferring' | 'filler') =>
      destroyAudioClipText(id, { company_id: companyId!, clip_type }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.AGENT_AUDIO_CLIP_TEXTS(id, companyId) });
    },
  });

  return { resetText, isPending };
};
